import _ from "lodash";
import {
  DEFAULT_SELECTED_PRODUCT_CODE,
  doDemandedActions,
  FormProperties,
  LOCATION,
  MASTERGROUP,
  MESSAGE,
  OPERATION,
  undoLastActionsRescursively,
} from "./businesslogic/hardwiredlogic";
import moment from "moment";
import NormalText from "../general/NormalText";
import "moment/locale/fr"; // Import French locale data
import { current } from "@reduxjs/toolkit";

// this will give back a new formstate which will have all the current nodes for all forms to set to the first of its nodes in the list
export const setCurrentNodesToFirstOfItsNodestate = ({
  formstate,
  formsorder,
}) => {
  try {
    formstate.currentnode =
      formstate[formsorder[0]].nodestate[
        formstate[formsorder[0]].nodesorder[0]
      ];
  } catch (e) {}

  try {
    formsorder.forEach((formid) => {
      formstate[formid].currentnode =
        formstate[formid].nodestate[formstate[formid].nodesorder[0]];
    });
  } catch (e) {}

  return { formstate, formsorder };
};

// this function will change state as per command in logic of question
const handleAnswerCommonFlow = ({
  qf,
  node,
  demandedActions,
  changedValue,
  isBuilding,
}) => {
  const { formid, id } = node;

  // //#region undo last actions
  qf = undoLastActionsRescursively({
    qf: { ...qf },
    node,
    // sending demaned actions as one of the form-remove-policy rule is this:
    // If the demanadedActions has ADDFORMS = ["e_9100"] and the lastActions contain
    // ADDFORMS = ["e_9100", "e_abcd"], then the undoing nullifies to only ADDFORMS ["e_abcd"].
    // This way remvoing a particular form and adding the same one can be avoided. This saves users already answered values in the form.
    demandedActions,
  });

  //#endregion
  //

  try {
    qf = doDemandedActions({
      demandedActions,
      qf: { ...qf },
      node,
      isBuilding,
    });
  } catch (error) {
    console.error("Error occurred in doDemandedActions:", error);
    // You can also handle the error more specifically here if needed
  }
  //#endregion
  if (qf.formstate[formid] && qf.formstate[formid].nodestate[id])
    qf.formstate[formid].nodestate[id].answer = changedValue;

  return qf;
};

export const setCurrentNodeIfEmpty = ({ qf }) => {
  if (_.isEmpty(qf.doc.formstate.currentnode)) {
    qf.doc.formstate.currentnode = qf.doc.formstate[qf.doc.formsorder[0]]
      ?.currentnode
      ? qf.doc.formstate[qf.doc.formsorder[0]]?.currentnode
      : {};
  }
};

// Helper function to validate and clean address data before caching
const isValidAddressData = (addressData) => {
  if (!addressData || typeof addressData !== 'object') return false;
  
  // Check if at least one meaningful field has content
  const meaningfulFields = ['street1', 'street', 'town', 'province', 'country', 'postalcode'];
  return meaningfulFields.some(field => 
    addressData[field] && 
    typeof addressData[field] === 'string' && 
    addressData[field].trim().length > 0
  );
};

// Helper function to create a clean copy of address data
const cleanAddressData = (addressData) => {
  if (!addressData) return null;
  
  const cleanData = {};
  const addressFields = ['streetNumber', 'street1', 'street', 'town', 'province', 'country', 'postalcode', 'unitNumber'];
  
  addressFields.forEach(field => {
    cleanData[field] = (addressData[field] && typeof addressData[field] === 'string') 
      ? addressData[field].trim() 
      : "";
  });
  
  return cleanData;
};

export const handleYesNoAnswerChange = ({ qf, node, changedValue }) => {
  const { formid, locationid, questionid } = node;


  if (locationid && questionid === "500100") {

    const { logic } = node;
    let demandedActions = logic[changedValue];
    let updatedQf = handleAnswerCommonFlow({ qf, node, demandedActions, changedValue });


    const locationAddressNode = updatedQf.formstate[formid].nodestate[`location_${locationid}_500110`];
    //console.log("out of shape", locationAddressNode, updatedQf.formstate[formid].nodestate);
    
    // Add comprehensive safety checks
    if (locationAddressNode && updatedQf.formstate[formid] && updatedQf.formstate[formid].nodestate) {
      if (changedValue === "Yes") {
        // Before copying main address, save the current custom address if it has valid data
        if (isValidAddressData(locationAddressNode.answer)) {
          locationAddressNode.customAnswer = cleanAddressData(locationAddressNode.answer);
          console.log("Cached custom address when switching to Yes:", locationAddressNode.customAnswer);
        }
        
        // Copy the main address into the new node's answer with safety check
        const mainAddressNode = updatedQf.formstate["a"]?.nodestate?.["10505"];
        if (mainAddressNode && mainAddressNode.answer) {
          locationAddressNode.answer = cleanAddressData(mainAddressNode.answer);
        } else {
          console.warn("Main address node not found or has no answer, using empty address");
          locationAddressNode.answer = {
            streetNumber: "", street1: "", street: "", town: "",
            province: "", country: "", postalcode: "", unitNumber: ""
          };
        }
      } else { // changedValue is "No"
        // Restore the cached address from `customAnswer` or set a default empty object
        if (locationAddressNode.customAnswer && isValidAddressData(locationAddressNode.customAnswer)) {
          locationAddressNode.answer = cleanAddressData(locationAddressNode.customAnswer);
          console.log("Restored custom address when switching to No:", locationAddressNode.answer);
        } else {
          // Set default empty address structure
          locationAddressNode.answer = {
            streetNumber: "", street1: "", street: "", town: "",
            province: "", country: "", postalcode: "", unitNumber: ""
          };
        }
      }
    } else {
      console.warn("Location address node not found or form state is invalid", {
        formid,
        locationid,
        nodeExists: !!locationAddressNode,
        formStateExists: !!(updatedQf.formstate[formid] && updatedQf.formstate[formid].nodestate)
      });
    }

    return updatedQf;

  } else {
    // If it's NOT our special location question, run the original logic exactly as it was.
    const { logic } = node;
    let demandedActions = logic[changedValue];
    qf = handleAnswerCommonFlow({ qf, node, demandedActions, changedValue });
    return qf;
  }
};

export const handleBundleSetAnswerChange = ({ qf, node, changedValue }) => {
  const {formid, locationid, questionid } = node;
  let id = node.id;
  let answer = qf.formstate[formid].nodestate[id].answer; 
  if(!answer){
    answer = { mainanswer: null, selectedoptions : [] };
    qf.formstate[formid].nodestate[id].answer = answer;
  } 
  if(["Yes", "No"].includes(changedValue) ) {
    answer.mainanswer = changedValue;
    if(changedValue == "No"){
      if(Array.isArray(answer.selectedoptions)){
        // need to loop through existing selectedoptions and revese demandActions
        for(let optionId of answer.selectedoptions){
          let optionNode = qf.formstate[formid].nodestate[optionId];
          const { logic } = optionNode;
          let demandedActions = logic[changedValue];
          qf = handleAnswerCommonFlow({ qf, node:optionNode, demandedActions, changedValue });
        }
      }
      answer.selectedoptions = [];
    }
  }
  else {
    //answer being passed is from one of the options
    let selectedOptions = answer.selectedoptions;
    const index = answer.selectedoptions.indexOf(changedValue);

    if (index !== -1) {
      selectedOptions.splice(index, 1); // Modify original array
    } else if (  answer.mainanswer == "Yes" ) {
      selectedOptions.push(changedValue); // Modify original array
    }
    // we need to send question off to questionEngine to do any demandedActions
    // first get node of this id which is changedValue
    let optionNode = qf.formstate[formid].nodestate[changedValue];
    //console.log("optionNode ", current(optionNode));
    if(optionNode) {
      if(optionNode.type == 'yesno'){
        const { logic } = optionNode;
        let optionNodeAnswer = selectedOptions.includes(changedValue) ? "Yes": "No"
        let demandedActions = logic[optionNodeAnswer];
        //console.log("Handle Anser commont flow")
        qf = handleAnswerCommonFlow({ qf, node:optionNode, demandedActions, changedValue: optionNodeAnswer });
      }
    }
  }
  
  return qf;
}

export const handleSimpleStringAnswerChange = ({ qf, node, changedValue }) => {
  const { logic } = node;
  let demandedActions = {};
  let demandedActionsEmptyBool = true;
  try {
    if (logic?.["booleanlogic"]) {
      if (logic["booleanlogic"]["equal"]) {
        if (
          logic["booleanlogic"]["equal"][changedValue] &&
          changedValue != ""
        ) {
          demandedActions = logic["booleanlogic"]["equal"][changedValue];
          demandedActionsEmptyBool = false;
        }
      }
      if (logic["booleanlogic"]["lt"] && demandedActionsEmptyBool) {
        let LessThanValues = Object.keys(logic["booleanlogic"]["lt"]);
        for (let LessThanValue of LessThanValues) {
          if (LessThanValue != "" && parseInt(changedValue) < LessThanValue) {
            demandedActions = logic["booleanlogic"]["lt"][LessThanValue];
            demandedActionsEmptyBool = false;
          }
        }
      }
      if (logic["booleanlogic"]["gt"] && demandedActionsEmptyBool) {
        let GreaterThanValues = Object.keys(logic["booleanlogic"]["gt"]);
        for (let GreaterThanValue of GreaterThanValues) {
          if (
            GreaterThanValue != "" &&
            parseInt(changedValue) > parseInt(GreaterThanValue)
          ) {
            demandedActions = logic["booleanlogic"]["gt"][GreaterThanValue];
            demandedActionsEmptyBool = false;
          }
        }
      }
      if (logic["booleanlogic"]["includes"] && demandedActionsEmptyBool) {
        let IncludesValues = Object.keys(logic["booleanlogic"]["includes"]);
        for (let IncludeValue of IncludesValues) {
          if (IncludeValue != "" && IncludeValue.includes(changedValue)) {
            demandedActions = logic["booleanlogic"]["includes"][IncludeValue];
            demandedActionsEmptyBool = false;
          }
        }
      }
    }
  } catch (e) {
    console.log(e);
  }
  qf = handleAnswerCommonFlow({ qf, node, demandedActions, changedValue });
  return qf;
};

export const handleSingleSelectAnswerChange = ({
  qf,
  node,
  changedValue,
  isBuilding,
}) => {
  // IMP NOTE: EVERY QUESTION TYPE CAN BE IN ANY FORMTYPE, so every question answer change handle
  // should be designed as per form type. This function will take care of all the forms whether it is
  // mastergroup, operation or location.

  let demandedActions = changedValue?.actions;

  qf = handleAnswerCommonFlow({
    qf,
    node,
    demandedActions,
    changedValue,
    isBuilding,
  });

  return qf;
};

export const handleMultiSelectAnswerChange = ({ qf, node, changedValue }) => {
  // IMP NOTE: EVERY QUESTION TYPE CAN BE IN ANY FORMTYPE, so every question answer change handle
  // should be designed as per form type. This function will take care of all the forms whether it is
  // mastergroup, operation or location.

  const aggregateMultipleDemandedActionsIntoOne = ({ changedValue }) => {
    let aggregatedDemandedActions = {};
    if (!_.isEmpty(changedValue)) {
      changedValue.forEach((option) => {
        if (option.actions) {
          for (let action in option.actions) {
            if (typeof option.actions[action] === "object") {
              if (aggregatedDemandedActions[action]) {
                // merge with already existing one
                aggregatedDemandedActions[action] = [
                  ...new Set([
                    ...aggregatedDemandedActions[action],
                    ...option.actions[action],
                  ]),
                ];
              } else {
                aggregatedDemandedActions[action] = [...option.actions[action]];
              }
            }
          }
        }
      });
    }

    return aggregatedDemandedActions;
  };
  let demandedActions = aggregateMultipleDemandedActionsIntoOne({
    changedValue,
  });

  qf = handleAnswerCommonFlow({ qf, node, demandedActions, changedValue });

  return qf;
};

export const isAnswerEmpty = (answer) => {
  try {
    if (answer === "" || answer === null || answer === undefined) return true;
    return false;
  } catch (e) {
    return true;
  }
};
export const isCoverValueEmpty = (covervalue) => {
  try {
    if (covervalue === "" || covervalue === null || covervalue === undefined)
      return true;
    return false;
  } catch (e) {
    return true;
  }
};

const getQAnsweredValuesForAsingleForm = ({ nodesorder, nodestate }) => {
  let returnObject = {
    totalQs: 0,
    totalQAnswered: 0,
    totalImp: 0,
    totalImpAnswered: 0,
    totalUnAnsweredQuestion: 0,
    totalNonAnswerableQuestion: 0,
    totalAnswerableQuestions: 0,
  };
  returnObject.totalQs = nodesorder.length;

  // let testQues = [];
  nodesorder.forEach((nodeid) => {
    if (nodestate[nodeid].type === MESSAGE) {
      returnObject.totalNonAnswerableQuestion++;
    } else {
      let answer = null;
      let importance = null;
      try {
        answer = nodestate[nodeid].answer;
        importance = nodestate[nodeid].importance;
        let isImp = importance > 3 ? true : false;
        if (isImp) returnObject.totalImp++;
        if (
          answer === "" ||
          answer === null ||
          answer === undefined ||
          answer === [] ||
          answer === {}
        ) {
          returnObject.totalUnAnsweredQuestion++;
          //
          // testQues.push(nodestate[nodeid]);
        } else {
          returnObject.totalQAnswered++;
          if (isImp) returnObject.totalImpAnswered++;
        }
      } catch (e) {
        // if there is no answer in the node
        returnObject.totalUnAnsweredQuestion++;
        //
        // testQues.push(nodestate[nodeid]);
      }
      returnObject.totalAnswerableQuestions++;
      //
    }
  });

  // returnObject.testQues = testQues;
  return returnObject;
};

const getAllFormIdsWhoseNodesLengthIsChanged = ({
  formstate,
  formsorder,
  trackingQAnsweredEachForm,
}) => {
  let formids = new Set();

  formsorder.forEach((formid) => {
    try {
      if (
        formstate[formid].nodesorder.length !==
        trackingQAnsweredEachForm[formid].totalQs
      )
        formids.add(formid);
    } catch (e) {
      formids.add(formid);
    }
  });

  return [...formids];
};

export const updateTrackingQAnsweredFinalValuesHelper = ({
  formsorder,
  formstate,
  trackingQAnsweredEachForm,
  trackingQAnsweredFinalValues,
  formChangedId,
}) => {
  let rValue = {};

  //#region get the difference between formsorer and formsorderConsidered from trackingQAnsweredFinalValues
  let formset = new Set(trackingQAnsweredFinalValues.formsorderConsidered);
  let diff = new Set();

  //#region first: add the form id that is changed
  if (formChangedId) diff.add(formChangedId);
  //#endregion

  //#region second: add the difference from formsorder
  formsorder.forEach((formid) => {
    if (!formset.has(formid)) diff.add(formid);
  });
  //#endregion

  //#region third: add all formids whose nodesorder length is changed
  getAllFormIdsWhoseNodesLengthIsChanged({
    formsorder,
    formstate,
    trackingQAnsweredEachForm,
  }).forEach((formid) => {
    diff.add(formid);
  });
  //#endregion

  //#endregion

  //#region add information in trackingQAnswredEachForm an object with relevant vaues
  diff.forEach((formid) => {
    trackingQAnsweredEachForm[formid] = getQAnsweredValuesForAsingleForm({
      nodesorder: formstate[formid].nodesorder,
      nodestate: formstate[formid].nodestate,
    });
  });

  //#endregion

  //#region  update trackingQAnsweredFinalValues

  trackingQAnsweredFinalValues = {
    totalQs: 0,
    totalQAnswered: 0,
    totalImp: 0,
    totalImpAnswered: 0,
    totalUnAnsweredQuestion: 0,
    totalNonAnswerableQuestion: 0,
    totalAnswerableQuestions: 0,
    formsorderConsidered: [...formsorder],
  };

  formsorder.forEach((formid) => {
    trackingQAnsweredFinalValues.totalQs +=
      trackingQAnsweredEachForm[formid].totalQs;
    trackingQAnsweredFinalValues.totalQAnswered +=
      trackingQAnsweredEachForm[formid].totalQAnswered;
    trackingQAnsweredFinalValues.totalImp +=
      trackingQAnsweredEachForm[formid].totalImp;
    trackingQAnsweredFinalValues.totalImpAnswered +=
      trackingQAnsweredEachForm[formid].totalImpAnswered;
    trackingQAnsweredFinalValues.totalUnAnsweredQuestion +=
      trackingQAnsweredEachForm[formid].totalUnAnsweredQuestion;
    trackingQAnsweredFinalValues.totalNonAnswerableQuestion +=
      trackingQAnsweredEachForm[formid].totalNonAnswerableQuestion;
    trackingQAnsweredFinalValues.totalAnswerableQuestions +=
      trackingQAnsweredEachForm[formid].totalAnswerableQuestions;
  });
  //#endregion

  rValue = { trackingQAnsweredEachForm, trackingQAnsweredFinalValues };

  return rValue;
};

export const updateNodeTime = (formstate, node) => {
  let formid = node?.formid;
  let nodeid = node?.id;

  // VERY IMP: update the current form node id as well

  if (formstate[formid]) {
    formstate[formid].currentnode = node;
    if (formstate[formid].nodestate[nodeid]){
      formstate[formid].nodestate[nodeid].time = new Date().getTime();
      return formstate[formid].nodestate[nodeid];
    }
  }
  return node;
};

export const arrayToObject = (array, keyField) =>
  array.reduce((obj, item) => {
    obj[item[keyField]] = item;
    return obj;
  }, {});

export const arrayToObjectForMyQuotes = (array, keyField) =>
  array.reduce((obj, item) => {
    item.clientdetails = PossibleClientDetailsStatus.notstarted;

    obj[item[keyField]] = item;

    return obj;
  }, {});

export const objectToSingleElementArray = (object) => {
  let singleelementarray = [];
  for (let key in object) {
    singleelementarray.push(key);
  }

  return singleelementarray;
};

export const convertArrayOfObjectsToId = (array, keyField) => {
  return array.map((a) => a[keyField]);
};

export const filterOnlyManualInsurer = (array, calculatedverticalid) => {
  return array.filter(
    (insurer) =>
      insurer.quotesby === "manual" &&
      insurer.isactiveforusers &&
      insurer.verticalids.includes(calculatedverticalid)
  );
};

export const filterOnlyManualInsurerInverseVertical = (
  array,
  calculatedverticalid
) => {
  return array.filter(
    (insurer) =>
      insurer.quotesby === "manual" &&
      insurer.isactiveforusers &&
      insurer.isactive &&
      !insurer.verticalids.includes(calculatedverticalid)
  );
};

export const insurerarrayToObject = (array, keyField) =>
  array.reduce((obj, item) => {
    item.selected = false;
    // includance can have six values:
    // null, "awaiting", "includedinpast", "includedthistime", "neverincluded", "failed"
    item.includance = null;
    if (!item.emails) {
      item.emails = [];
      item.selectedemail = "";
    } else if (item.emails.length === 0) {
      item.selectedemail = "";
    } else if (item.emails.length > 0) {
      let defaultemail = item.emails.filter(
        (e) => e.default === "yes" || e.default === "Yes"
      );

      if (defaultemail.length === 0) {
        item.selectedemail = "";
      } else if (defaultemail.length > 0) {
        item.selectedemail = defaultemail[0].email;
      }
    }

    obj[item[keyField]] = item;
    return obj;
  }, {});

export const removeElementFromArray = (array, item) => {
  var index = array.indexOf(item);
  if (index !== -1) {
    array.splice(index, 1);
  }
};

export const setSomeInitialValuesToResponseDetails = (responsedetails) => {
  if (_.isEmpty(responsedetails)) return {};

  let editedresponsedetails = {};
  for (let responseid in responsedetails) {
    editedresponsedetails[responseid] = {
      ...responsedetails[responseid],
      quoteyscore: PossibleQuoteyScoreValues.nullvalue,
    };
  }

  return editedresponsedetails;
};

export const setSelectedToFalseExceptPackage = (productlist) => {
  if (!productlist) return [];
  if (_.isEmpty(productlist)) return [];

  let newProductlist = [];
  productlist.forEach((product) => {
    newProductlist.push({
      ...product,
      selected: product.code === DEFAULT_SELECTED_PRODUCT_CODE ? true : false,
    });
  });

  return newProductlist;
};

export const setSelectedToFalseExceptSelected = (productlist, selectedcode) => {
  if (!productlist) return [];
  if (_.isEmpty(productlist)) return [];

  let newProductlist = [];
  productlist.forEach((product) => {
    newProductlist.push({
      ...product,
      selected: product.code === selectedcode ? true : false,
    });
  });

  return newProductlist;
};

const getGroupNodesOrder = ({ formstate, groupid }) => {
  let { nodesorder, nodestate } = formstate;

  let groupnodesorder = [];
  nodesorder.forEach((nodeid) => {
    if (nodestate[nodeid].groupid === groupid) groupnodesorder.push(nodeid);
  });

  return groupnodesorder;
};

const getLocationGroupNodesOrder = ({ formstate, groupid, locationid }) => {
  let { nodesorder, nodestate } = formstate;

  let groupnodesorder = [];
  nodesorder.forEach((nodeid) => {
    if (
      nodestate[nodeid].groupid === groupid &&
      nodestate[nodeid].locationid === locationid
    )
      groupnodesorder.push(nodeid);
  });

  return groupnodesorder;
};

const getLocationNodesOrder = ({ formstate, locationid }) => {
  let { nodesorder, nodestate } = formstate;

  let locationnodesorder = [];
  nodesorder.forEach((nodeid) => {
    if (nodestate[nodeid].locationid === locationid)
      locationnodesorder.push(nodeid);
  });

  return locationnodesorder;
};

const getBuildingNodesOrder = ({ formstate, locationid, buildingid }) => {
  let { nodesorder, nodestate } = formstate;

  let locationnodesorder = [];
  nodesorder.forEach((nodeid) => {
    if (
      parseInt(nodestate[nodeid].locationid) === parseInt(locationid) &&
      nodestate[nodeid].buildingid === buildingid
    )
      locationnodesorder.push(nodeid);
  });

  return locationnodesorder;
};

const getLastAccessedNodeByUserAsPerTime = ({ nodesorder, nodestate }) => {
  let latestNodeId = nodesorder[0];
  nodesorder.forEach((nodeid) => {
    if (nodestate[nodeid].time > nodestate[latestNodeId].time)
      latestNodeId = nodeid;
  });
  return nodestate[latestNodeId];
};

export const figureOutNextNodeOnDirectGroupClick = ({ formstate, groupid }) => {
  let currentnode = formstate.currentnode;
  let currentformid = currentnode.formid;

  let nodestate = { ...formstate[currentformid].nodestate };

  let nodesorder = getGroupNodesOrder({
    formstate: { ...formstate[currentformid] },
    groupid,
  });

  let lastAccessedNodeByUserInGroup = getLastAccessedNodeByUserAsPerTime({
    nodesorder,
    nodestate,
  });

  if (lastAccessedNodeByUserInGroup) return lastAccessedNodeByUserInGroup;

  return null;
};

export const figureOutNextNodeOnDirectLocationGroupClick = ({
  formstate,
  groupid,
  locationid,
}) => {
  let currentnode = formstate.currentnode;
  let currentformid = currentnode.formid;

  let nodestate = { ...formstate[currentformid].nodestate };

  let nodesorder = getLocationGroupNodesOrder({
    formstate: { ...formstate[currentformid] },
    groupid,
    locationid,
  });

  let lastAccessedNodeByUserInGroup = getLastAccessedNodeByUserAsPerTime({
    nodesorder,
    nodestate,
  });

  if (lastAccessedNodeByUserInGroup) return lastAccessedNodeByUserInGroup;

  return null;
};

export const figureOutNextNodeOnDirectLocationClick = ({
  formstate,
  locationid,
}) => {
  let currentnode = formstate.currentnode;
  let currentformid = currentnode.formid;

  let nodestate = { ...formstate[currentformid].nodestate };

  let nodesorder = getLocationNodesOrder({
    formstate: { ...formstate[currentformid] },
    locationid,
  });

  let lastAccessedNodeByUserInGroup = getLastAccessedNodeByUserAsPerTime({
    nodesorder,
    nodestate,
  });

  if (lastAccessedNodeByUserInGroup) return lastAccessedNodeByUserInGroup;

  return null;
};

export const figureOutNextNodeOnDirectBuildingClick = ({
  formstate,
  locationid,
  buildingid,
}) => {
  let currentnode = formstate.currentnode;
  let currentformid = currentnode.formid;

  let nodestate = { ...formstate[currentformid].nodestate };

  //
  let nodesorder = getBuildingNodesOrder({
    formstate: { ...formstate[currentformid] },
    locationid,
    buildingid,
  });

  //

  let lastAccessedNodeByUserInGroup = getLastAccessedNodeByUserAsPerTime({
    nodesorder,
    nodestate,
  });

  if (lastAccessedNodeByUserInGroup) return lastAccessedNodeByUserInGroup;

  return null;
};

const findOutNexNodeForCurrentForm = ({ formstate }) => {
  let currentnode = formstate.currentnode;
  let formid = currentnode.formid;
  let nodestate = formstate[formid].nodestate;
  let nodesorder = formstate[formid].nodesorder;

  let nextNode = null;
  let index = nodesorder.indexOf(currentnode.id);

  if (index >= 0 && index < nodesorder.length)
    nextNode = nodestate[nodesorder[index + 1]];

  if (nextNode) return nextNode;

  return null;
};

const findOutPreviousNodeForCurrentForm = ({ formstate }) => {
  let currentnode = formstate.currentnode;
  let formid = currentnode.formid;
  let nodestate = formstate[formid].nodestate;
  let nodesorder = formstate[formid].nodesorder;

  let previousNode = null;
  let index = nodesorder.indexOf(currentnode.id);

  if (index >= 0 && index < nodesorder.length)
    previousNode = nodestate[nodesorder[index - 1]];

  if (previousNode) return previousNode;

  return null;
};

const getNextFormFirstGroupFirstNode = ({
  formstate,
  formsorder,
  currentformid,
}) => {
  try {
    let currentFormIdIndex = formsorder.indexOf(currentformid);

    let nextFormId = formsorder[currentFormIdIndex + 1];

    if (nextFormId) {
      return formstate[nextFormId].nodestate[
        formstate[nextFormId].nodesorder[0]
      ];
    }

    return null;
  } catch (e) {
    console.error("error getNextFormFirstGroupFirstNod", e);
    return null;
  }
};

const findFirstNodeWithThisGroupIdFromNodesOrder = ({
  nodesorder,
  nodestate,
  groupid,
}) => {
  try {
    let i = nodesorder.length - 1;
    let lastNode = null;
    while (i >= 0) {
      if (nodestate[nodesorder[i]].groupid === groupid) {
        lastNode = nodestate[nodesorder[i]];
      } else {
        return lastNode;
      }
      i--;
    }
    return lastNode;
  } catch (e) {
    return null;
  }
};
const getPreviousFormLastGroupFirstNode = ({
  formstate,
  formsorder,
  currentformid,
}) => {
  try {
    let currentFormIdIndex = formsorder.indexOf(currentformid);

    let previousFormId = formsorder[currentFormIdIndex - 1];

    if (previousFormId) {
      let lastNode =
        formstate[previousFormId].nodestate[
          formstate[previousFormId].nodesorder[
            formstate[previousFormId].nodesorder.length - 1
          ]
        ];
      if (lastNode) {
        let lastNodeGroupId = lastNode.groupid;
        let firstNodeWithLastGroupId =
          findFirstNodeWithThisGroupIdFromNodesOrder({
            nodesorder: [...formstate[previousFormId].nodesorder],
            nodestate: { ...formstate[previousFormId].nodestate },
            groupid: lastNodeGroupId,
          });

        if (firstNodeWithLastGroupId) return firstNodeWithLastGroupId;

        return null;
      }
      return null;
    }

    return null;
  } catch (e) {
    return null;
  }
};

export const figureOutNextNodeOnNextClick = ({
  formsorder,
  formstate,
  isExpressApplication,
  nonPriorityGroups,
}) => {
  try {
    let { currentnode } = formstate;
    let { formtype, formid } = currentnode;

    let nextGroupFirstNodeForCurrentForm = FormProperties({
      formtype,
    }).getNextNodeOnNextClick({ formstate: formstate[formid], currentnode });
    //
    if (nextGroupFirstNodeForCurrentForm) {
      // if expressApplication and entire group needs to be hidden, go to next group after that
      if (
        isExpressApplication &&
        nonPriorityGroups.includes(nextGroupFirstNodeForCurrentForm.groupid)
      ) {
        let avoidNull = true;
        let prev_nextGroupFirstNodeForCurrentForm = nextGroupFirstNodeForCurrentForm;
        try{
          while(nextGroupFirstNodeForCurrentForm && nonPriorityGroups.includes(nextGroupFirstNodeForCurrentForm.groupid) && avoidNull ){            
            nextGroupFirstNodeForCurrentForm = FormProperties({
              formtype,
            }).getNextNodeOnNextClick({
              formstate: formstate[formid],
              currentnode: nextGroupFirstNodeForCurrentForm,
            });
            //console.log(current(nextGroupFirstNodeForCurrentForm))
            if( !nextGroupFirstNodeForCurrentForm ){
              //nextGroupFirstNodeForCurrentForm = prev_nextGroupFirstNodeForCurrentForm;
              avoidNull = false;
            }
            else{
              prev_nextGroupFirstNodeForCurrentForm = nextGroupFirstNodeForCurrentForm;
            }  
          }
        }
        catch(e){
          console.log("INSIDE CATCH ", e)
        }    
      }
      if (nextGroupFirstNodeForCurrentForm)
        return nextGroupFirstNodeForCurrentForm;
    }

    let nextFormFirstGroupFirstNode = getNextFormFirstGroupFirstNode({
      formsorder,
      formstate,
      currentformid: formid,
    });
    let lastFormid = formsorder.at(-1);
    let lastGroupid = formstate[lastFormid]?.groupsorder.at(-1);
    // if expressApplication and entire form needs to be hidden, go to next form after that
    while (
      isExpressApplication &&
      nonPriorityGroups.includes(nextFormFirstGroupFirstNode.groupid) &&
      nextFormFirstGroupFirstNode.groupid != lastGroupid
    ) {
      nextFormFirstGroupFirstNode = FormProperties({
        formtype,
      }).getNextNodeOnNextClick({
        formstate: formstate[nextFormFirstGroupFirstNode.formid],
        currentnode: nextFormFirstGroupFirstNode,
      });
      // console.log(current(nextFormFirstGroupFirstNode))
    }
    if (nextFormFirstGroupFirstNode) return nextFormFirstGroupFirstNode;

    return null;
  } catch (e) {
    return null;
  }
};

export const figureOutPreviousNodeOnPreviousClick = ({
  formsorder,
  formstate,
  isExpressApplication,
  nonPriorityGroups,
}) => {
  try {
    let { currentnode } = formstate;
    let { formtype, formid } = currentnode;

    let previousGroupFirstNodeForCurrentForm = FormProperties({
      formtype,
    }).getPreviousNodeOnPreviousClick({
      formstate: formstate[formid],
      currentnode,
    });

    if (
      isExpressApplication &&
      nonPriorityGroups.includes(previousGroupFirstNodeForCurrentForm?.groupid)
    ) {
      let firstFormid = formsorder.at(1);
      let firstGroupid = formstate[firstFormid]?.groupsorder.at(1);
      while (
        isExpressApplication && previousGroupFirstNodeForCurrentForm &&
        nonPriorityGroups.includes(previousGroupFirstNodeForCurrentForm.groupid) &&
        previousGroupFirstNodeForCurrentForm.groupid != firstGroupid
      ) {
        previousGroupFirstNodeForCurrentForm = FormProperties({
          formtype,
        }).getPreviousNodeOnPreviousClick({
          formstate: formstate[previousGroupFirstNodeForCurrentForm.formid],
          currentnode: previousGroupFirstNodeForCurrentForm,
        });
        //if(previousGroupFirstNodeForCurrentForm)
          //console.log(current(previousGroupFirstNodeForCurrentForm))
      }
    }

    if (previousGroupFirstNodeForCurrentForm)
      return previousGroupFirstNodeForCurrentForm;

    let previousFormFirstGroupFirstNode = getPreviousFormLastGroupFirstNode({
      formsorder,
      formstate,
      currentformid: formid,
    });
    if (previousFormFirstGroupFirstNode) return previousFormFirstGroupFirstNode;

    return null;
  } catch (e) {
    //console.log(e)
    return null;
  }
};

export const PossibleIncludanceValues = {
  neverincluded: "neverincluded",
  includedinpast: "includedinpast",
  incudedthistime: "includedthistime",
  failed: "failed",
  awaiting: "awaiting",
  nullvalue: null,
};

export const PossibleQuoteyScoreValues = {
  loading: "loading",
  nullvalue: null,
};

export const PossibleCoverValues = {
  loading: "loading",
  nullvalue: null,
};
export const PossibleApiStatusValues = {
  loading: "loading",
  nullvalue: null,
  failed: "failed",
  success: "success",
  notstarted: "notstarted",
};
export const PossibleExcelApiStatusValues = {
  loading: "loading",
  nullvalue: null,
  failed: "failed",
  success: "success",
  notstarted: "notstarted",
  // and there is object
};

export const PossibleClientDetailsStatus = {
  loading: "loading",
  nullvalue: null,
  failed: "failed",
  success: "success",
  notstarted: "notstarted",
  // and there is object
};

export const PossibleCoverMatchValues = {
  loading: "loading",
  nullvalue: null,
};

// Why? This function will grab all the permanent codes in on single object
export const grabAllPermanentCodeQuestionAtOnePlace = ({
  formsorder,
  formstate,
}) => {
  // this will contain the information of the whole node
  let allPermanentCodeAtOnePlace = {};

  formsorder.forEach((formid) => {
    if (formstate[formid]) {
      let nodesorder = formstate[formid].nodesorder;
      let nodestate = formstate[formid].nodestate;

      if (!_.isEmpty(nodesorder) && Array.isArray(nodesorder)) {
        nodesorder.forEach((nodeid) => {
          try {
            if (nodestate[nodeid]["permanentcode"]) {
              allPermanentCodeAtOnePlace[nodestate[nodeid]["permanentcode"]] =
                nodestate[nodeid];
            }
          } catch (e) {
            console.log(e);
          }
        });
      }
    }
  });

  return allPermanentCodeAtOnePlace;
};

//#region CompareView helpers

export const PossibleCols = {
  COVERINFO: "coverinfo",
  CLIENTNEEDS: "clientneeds",
  INSURER: "insurer",
};

export const coverTableToCoverTypeObject = ({ covers }) => {
  let objectIntermidiate = {};

  covers.forEach((cover) => {
    if (!(cover.covertypecode in objectIntermidiate)) {
      objectIntermidiate[cover.covertypecode] = [];
    }
    // push this cover in that object
    objectIntermidiate[cover.covertypecode].push(cover);
  });

  return objectIntermidiate;
};

export const PossibleRowTypesForViewCompareTable = {
  CCHEADING: "covercodetypeheading",
  DATAROW: "datarow",
  TABLEHEADING: "tableheading",
  LOCATIONHEADING: "locationheading",
  LOCATIONDATAROW: "locationdatarow",
};

const generateLocationAddressFromObject = ({ addressObject }) => {
  if (!_.isEmpty(addressObject)) {
    return `${addressObject.street}, ${addressObject.town} - ${addressObject.postalcode}`;
  }
  return "";
};

export const coverTypeObjectToRows = ({
  covertypeobject,
  locationAddresses,
}) => {
  let rows = [];
  let covertypeexpandprop = {};
  let locationExpandProp = {};
  for (let covertypecode in covertypeobject) {
    let dataRows = covertypeobject[covertypecode];
    covertypeexpandprop[covertypecode] = { expand: true };
    // This is where I will edit the modals for location buildings
    if (covertypecode === "LOCATION") {
      locationAddresses?.order?.forEach((locationid) => {
        // put the expand property in expandLocation to false
        locationExpandProp[locationid] = { expand: false };
        rows.push({
          rowtype: PossibleRowTypesForViewCompareTable.LOCATIONHEADING,
          name: `Location - ${locationid} ${generateLocationAddressFromObject({
            addressObject: locationAddresses.state[locationid].address,
          })}`,
          locationid,
          numberOfBuildings: locationAddresses.state[locationid]
            .numberOfBuildings
            ? locationAddresses.state[locationid].numberOfBuildings
            : 0,
        });
        dataRows.forEach((row) => {
          rows.push({
            ...row,
            rowtype: PossibleRowTypesForViewCompareTable.LOCATIONDATAROW,
            locationid,
          });
        });
      });
    } else {
      if (covertypecode === "RESPONSE")
        covertypeexpandprop[covertypecode] = { expand: true };
      rows.push({
        covertypecode,
        rowtype: PossibleRowTypesForViewCompareTable.CCHEADING,
      });

      dataRows.forEach((row) => {
        rows.push({
          ...row,
          rowtype: PossibleRowTypesForViewCompareTable.DATAROW,
        });
      });
    }
  }

  return {
    rows,
    covertypeexpandprop,
    locationExpandProp,
  };
};

export const convertResponsesArrayToObjects = ({ responses }) => {
  let returnedObject = {};

  responses.forEach((response) => {
    returnedObject[response.insurercode] = { ...response };
  });

  return returnedObject;
};

export const sequenceOfResponsesArray = ({ responses }) => {
  let returnedArray = [];

  responses.forEach((response) => {
    returnedArray.push(response.insurercode);
  });

  return returnedArray;
};

export const addResponseSeqToCols = ({ cols, responseSequence }) => {
  responseSequence.forEach((insurercode) => {
    cols.push({ type: PossibleCols.INSURER, insurercode });
  });

  return cols;
};

const getSortedLocationHeadings = (rows) => {
  return _.chain(rows)
    .filter({ rowtype: "locationheading" }) // Filter rows with rowtype of "locationheading"
    .keyBy("name") // Use the 'name' property as the key for each object
    .value(); // Convert the lodash wrapped object back to a regular JavaScript object
};

export const readyTheCards = (rows, hasBuilding = false, buildingCount) => {
  let newvaluecards = {};
  const sortedLocationHeadings = getSortedLocationHeadings(rows); // I need to get the relevant data to see if the current location has buildings or not

  for (let row = 0; row < rows?.length; row = row + 1) {
    if (rows[row].rowtype.includes("datarow")) {
      if (rows[row].rowtype.includes("location")) {
        const locid = rows[row].locationid;
        const buildingId = rows[rows]?.buildingId;
        const name = rows.find(
          (el) => el.locationid && el.locationid === locid
        ).name;
        //
        //
        //
        if (!hasBuilding) {
          newvaluecards = {
            ...newvaluecards,
            [name]: rows.filter(
              (item) =>
                item.locationid === locid && item.rowtype.includes("datarow")
            ),
          };
        } else {
          // Else, place the filtered rows into the Building-n structure.
          let buildingData = {};
          const numberOfBuildings = sortedLocationHeadings[name]
            .numberOfBuildings
            ? sortedLocationHeadings[name].numberOfBuildings
            : buildingCount[locid]?.buildingsCount;

          // const numberOfBuildings = buildingCount[locid]?.buildingCount;

          for (let i = 1; i <= numberOfBuildings; i++) {
            buildingData[`Building-${i}`] = rows.filter(
              (item) =>
                item.locationid === locid && item.rowtype.includes("datarow")
            );

            buildingData[`Building-${i}`] = buildingData[`Building-${i}`].map(
              (item) => ({
                ...item,
                buildingId: i,
                covervalue: "",
              })
            );

            // buildingData[`Building-${i}`] = {
            //   ...buildingData[`Building-${i}`],
            //   buildingId: i,
            // };
          }

          newvaluecards = {
            ...newvaluecards,
            [name]: buildingData,
          };

          //
        }
      } else {
        newvaluecards = {
          ...newvaluecards,
          [rows[row].covertypecode]: rows.filter(
            (item) =>
              item.covertypecode === rows[row].covertypecode &&
              item.rowtype.includes("datarow")
          ),
        };
      }
    }
  }

  Object.values(newvaluecards).forEach((el) => {
    if (Array.isArray(el)) {
      // If el is directly an array, sort it
      el.sort((a, b) => b.importance - a.importance);
    } else {
      // If el is an object containing building arrays, sort each of those arrays
      Object.values(el).forEach((buildingArray) => {
        buildingArray.sort((a, b) => b.importance - a.importance);
      });
    }
  });

  newvaluecards.RESPONSE = newvaluecards.RESPONSE?.filter((element) => {
    if (element.name == "Covermatch") return false;
    if (element.name == "Quotey Value Score") return false;
    return true;
  });

  return newvaluecards;
};
//# date format helper
export const FormateDate = ({ millliseconds, utc }) => {
  let date = new Date(millliseconds);
  let dateFormatted = moment(date, "DD/MM/YYYY").format("DD MMM YYYY");
  if (utc) {
    dateFormatted = moment.utc(date, "DD/MM/YYYY").format("DD MMM YYYY");
  }
  return (
    <NormalText
      text={dateFormatted}
      fontWeight={"normal"}
      fontSize={15}
      color="grey"
    ></NormalText>
  );
};

export const FormattedDate = ({ milliseconds, languagepreference }) => {
  let date = new Date(milliseconds);
  let dateFormatted = moment(date)
    .locale(languagepreference)
    .format("DD MMM YYYY");
  return (
    <NormalText
      text={dateFormatted}
      fontWeight={"normal"}
      fontSize={15}
      color="grey"
    ></NormalText>
  );
};
// date Utc time set helper
export function setReminderDate(sDate, eDate) {
  const startDate = new Date(sDate);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(eDate);
  endDate.setHours(23, 59, 59, 999);
  return { startDate, endDate };
}
//#endregion
// Helper function to handle the child-parent relationship logic for covers
export const processCoversData = (coverData) => {
  let covers = [];
  const rowsWithParentIds = coverData.filter((r) => r.parentid !== null);
  const rowsWithoutParentIds = coverData.filter((r) => r.parentid === null);

  for (let i = 0; i < coverData.length; i++) {
    if (coverData[i].parentid === undefined) {
      covers.push(coverData[i]);
    }

    if (coverData[i].parentid === null) {
      covers.push(coverData[i]);
      const filteredData = coverData
        .filter((r) => r.parentid === coverData[i].id)
        .sort((a, b) => a.id - b.id); // b - a for reverse sort
      covers = [...covers, ...filteredData];
    }
  }

  return covers;
};
